import { createAsyncThunk } from "@reduxjs/toolkit";
import {
    getAllBillingsByCaseId,
    getBillingById,
    createBilling,
    updateBilling,
    updateAgreedFees,
    updateInitialBill,
    updateFinalBill,
    updateReconciliation,
    deleteBilling,
    formatBillingData,
    validateBillingData,
    parseInvoiceIds
} from "../../../api/caseBillings_api";

// =========== GET ALL BILLINGS BY CASE ID ===========
export const getAllBillingsByCaseIdAction = createAsyncThunk(
    "billings/getAllBillingsByCaseId",
    async (caseDetailsID, { rejectWithValue }) => {
        try {
            const result = await getAllBillingsByCaseId(caseDetailsID);

            if (result.success) {
                return {
                    caseDetailsID,
                    billings: result.data,
                    count: result.count,
                    message: result.message
                };
            } else {
                return rejectWithValue({
                    message: result.message,
                    error: result.error
                });
            }
        } catch (error) {
            return rejectWithValue({
                message: "Failed to fetch billings",
                error: error.message
            });
        }
    }
);

// =========== GET SINGLE BILLING BY ID ===========
export const getBillingByIdAction = createAsyncThunk(
    "billings/getBillingById",
    async (billingID, { rejectWithValue }) => {
        try {
            const result = await getBillingById(billingID);

            if (result.success) {
                return {
                    billing: result.data,
                    message: result.message
                };
            } else {
                return rejectWithValue({
                    message: result.message,
                    error: result.error
                });
            }
        } catch (error) {
            return rejectWithValue({
                message: "Failed to fetch billing",
                error: error.message
            });
        }
    }
);

// =========== CREATE BILLING ===========
export const createBillingAction = createAsyncThunk(
    "billings/createBilling",
    async ({ caseDetailsID, formData }, { rejectWithValue }) => {
        try {
            let dataToSend;

            if (formData instanceof FormData) {
                dataToSend = formData;
                if (!dataToSend.has('caseDetailsID')) {
                    dataToSend.append('caseDetailsID', caseDetailsID);
                }
            } else {
                dataToSend = formatBillingData({
                    ...formData,
                    caseDetailsID: caseDetailsID || formData.caseDetailsID
                });
            }

            const result = await createBilling(dataToSend);

            if (result.success) {
                return {
                    billing: result.data,
                    message: result.message
                };
            } else {
                return rejectWithValue({
                    message: result.message,
                    error: result.error
                });
            }
        } catch (error) {
            return rejectWithValue({
                message: "Failed to create billing",
                error: error.message
            });
        }
    }
);

// =========== UPDATE BILLING ===========
export const updateBillingAction = createAsyncThunk(
    "billings/updateBilling",
    async ({ billingID, billingData, caseDetailsID }, { rejectWithValue }) => {
        try {
            let dataToSend;

            if (billingData instanceof FormData) {
                dataToSend = billingData;
                if (!dataToSend.has('caseDetailsID') && caseDetailsID) {
                    dataToSend.append('caseDetailsID', caseDetailsID);
                }
            } else {
                dataToSend = formatBillingData({
                    ...billingData,
                    caseDetailsID,
                });
            }

            const result = await updateBilling(billingID, dataToSend);

            if (result.success) {
                return {
                    billingID,
                    billing: result.data,
                    message: result.message
                };
            } else {
                return rejectWithValue({
                    message: result.message,
                    error: result.error
                });
            }
        } catch (error) {
            return rejectWithValue({
                message: "Failed to update billing",
                error: error.message
            });
        }
    }
);

// =========== UPDATE AGREED FEES ===========
export const updateAgreedFeesAction = createAsyncThunk(
    "billings/updateAgreedFees",
    async ({ billingID, data }, { rejectWithValue }) => {
        try {
            const result = await updateAgreedFees(billingID, data);

            if (result.success) {
                return {
                    billingID,
                    billing: result.data,
                    message: result.message
                };
            } else {
                return rejectWithValue({
                    message: result.message,
                    error: result.error
                });
            }
        } catch (error) {
            return rejectWithValue({
                message: "Failed to update agreed fees",
                error: error.message
            });
        }
    }
);

// =========== UPDATE INITIAL BILL ===========
export const updateInitialBillAction = createAsyncThunk(
    "billings/updateInitialBill",
    async ({ billingID, data }, { rejectWithValue }) => {
        try {

            const formattedData = formatBillingData(data);

            const result = await updateInitialBill(billingID, formattedData);

            if (result.success) {
                return {
                    billingID,
                    billing: result.data,
                    message: result.message
                };
            } else {
                return rejectWithValue({
                    message: result.message,
                    error: result.error
                });
            }
        } catch (error) {
            return rejectWithValue({
                message: "Failed to update initial bill",
                error: error.message
            });
        }
    }
);

// =========== UPDATE FINAL BILL ===========
export const updateFinalBillAction = createAsyncThunk(
    "billings/updateFinalBill",
    async ({ billingID, data }, { rejectWithValue }) => {
        try {
            const formattedData = formatBillingData(data);
            const result = await updateFinalBill(billingID, formattedData);

            if (result.success) {
                return {
                    billingID,
                    billing: result.data,
                    message: result.message
                };
            } else {
                return rejectWithValue({
                    message: result.message,
                    error: result.error
                });
            }
        } catch (error) {
            return rejectWithValue({
                message: "Failed to update final bill",
                error: error.message
            });
        }
    }
);

// =========== UPDATE RECONCILIATION ===========
export const updateReconciliationAction = createAsyncThunk(
    "billings/updateReconciliation",
    async ({ billingID, data }, { rejectWithValue }) => {
        try {


            const result = await updateReconciliation(billingID, data);

            if (result.success) {
                return {
                    billingID,
                    billing: result.data,
                    message: result.message
                };
            } else {
                return rejectWithValue({
                    message: result.message,
                    error: result.error
                });
            }
        } catch (error) {
            return rejectWithValue({
                message: "Failed to update reconciliation",
                error: error.message
            });
        }
    }
);

// =========== DELETE BILLING ===========
export const deleteBillingAction = createAsyncThunk(
    "billings/deleteBilling",
    async ({ billingID, updatedBy }, { rejectWithValue }) => {
        try {
            const result = await deleteBilling(billingID, updatedBy);

            if (result.success) {
                return {
                    billingID,
                    message: result.message
                };
            } else {
                return rejectWithValue({
                    message: result.message,
                    error: result.error
                });
            }
        } catch (error) {
            return rejectWithValue({
                message: "Failed to delete billing",
                error: error.message
            });
        }
    }
);

// =========== FORMAT BILLING DATA (Non-async utility action) ===========
export const formatBillingDataAction = (billingData) => ({
    type: "billings/formatBillingData",
    payload: formatBillingData(billingData)
});

// =========== PARSE INVOICE IDs (Non-async utility action) ===========
export const parseInvoiceIdsAction = (invoiceField) => ({
    type: "billings/parseInvoiceIds",
    payload: parseInvoiceIds(invoiceField)
});

// =========== VALIDATE BILLING DATA (Non-async utility action) ===========
export const validateBillingDataAction = (billingData) => ({
    type: "billings/validateBillingData",
    payload: validateBillingData(billingData)
});
